import { Client } from "@/Common/Api/Client";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { License } from "@/Common/Api/Employee/Dto/License";
import { ExternalBillingUserInfoDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingUserInfoDto";
import { ExternalBillingUserInfoResponse } from "@/Common/Api/ExternalBilling/Dto/Responses/ExternalBillingUserInfoResponse";
import { LicenseDto } from "@/Common/Api/Labs/Dto/Responses/LicenseDto";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Guid } from "@/Common/Models/Guid";
import { IReferralDoctorModel } from "@/Common/Models/IReferralDoctorModel";
import { TitleAndSpecialtyModel } from "@/Common/Models/MedfarDictionaries/TitleAndSpecialtyModel";
import { Person } from "@/Common/Models/Person/Person";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { TermsAndConditionsServiceNames } from "@/Common/Resources/Lookups/Enums/TermsAndConditionsServiceNames";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { DrugInteractionAlertsFilterOption } from "../../Employee/Enums/DrugInteractionAlertsFilterOption";
import { EmployeeType } from "../../Employee/Enums/EmployeeType";
import { AllowlistType } from "../Enums";

class EmployeeUIModel implements ICheckable {
    private cachedSpecialty: LicenceOrganisationSpecialisation;
    private serviceSpecializations: FlatLookupView[]; // !! serviceSpecializations stays private !!  -->  use GetSpecialisations()

    private externalBillingUser: ExternalBillingUserInfoDto = null;
    private externalBillingUserFetched: boolean = false;
    private serviceComboCache: ServiceOrComboTableItem<IComboOrServiceItem>[];
    private readonly isEmptyEmailAllowed = !cacheStore.ClinicSettings.EnableSSOAuthentication;

    public Id: Guid = null;
    public AlternativeId: string;
    public ContactId: Guid;
    public ContactFacetId: Guid;
    public Type: EmployeeType;
    public LastName: string;
    public Name: string;
    public Sites: Guid[] = [];
    public Checked: boolean;
    public Visible: boolean = true;
    public Target: CheckboxTarget = CheckboxTarget.Unassigned;
    public Model: Employee = new Employee();
    public HasPatientNoteAccess: boolean;
    public PreferedFaxAccountNumberId?: number = null;
    public DisableTimers: boolean;
    public linkValid: boolean;
    public ProfileImageVersion: string;
    public DrugInteractionAlertsFilterOption: DrugInteractionAlertsFilterOption;

    constructor(fields?: Partial<EmployeeUIModel>) {
        (Object as any).assign(this, fields);
    }

    public get ServicesCombos(): ServiceOrComboTableItem<IComboOrServiceItem>[] {
        if (this.serviceComboCache)
            return this.serviceComboCache;

        this.refreshServiceCombos();
        return this.serviceComboCache ?? [];
    }

    public clone(): EmployeeUIModel {
        // TODO: this doesn't actually clone the UI model, it creates a new one from the underlying model.
        let model = JSON.parse(JSON.stringify(this.Model));
        if (model?.Licences?.any())
            model.Licences = model.Licences.map((license: Partial<License>) => new License(license));
        const employeeModel = EmployeeUIModel.fromCompositeObject(model);
        employeeModel.ProfileImageVersion = this.ProfileImageVersion;
        employeeModel.Checked = this.Checked;
        return employeeModel;
    }

    public getBillingProviderResolved(): BillingProvider | null {
        return BillingProviderHelper.resolveBillingProvider(this.Model.BillingProvider);
    }

    public getBillingProvider(): BillingProvider | null {
        return BillingProviderHelper.getBillingProvider(this.Model.BillingProvider);
    }

    public async getExternalBillingUserAsync(): Promise<ExternalBillingUserInfoDto | null> {
        if (!this.externalBillingUserFetched) {
            const response = await Client.postAsync<ExternalBillingUserInfoResponse>(
                ServerOperations.Server.ExternalBilling_GetUserInfo,
                {
                    BillingProvider: this.getBillingProviderResolved(),
                    EmployeeId: this.ContactFacetId
                }
            );

            this.externalBillingUser = response.data.BillingUserInfo;
            this.externalBillingUserFetched = true;
        }

        return this.externalBillingUser;
    }

    public setExternalBillingUser(billingUser: ExternalBillingUserInfoDto | null): void {
        this.Model.BillingProvider = EnumHelper.getName(BillingProvider, billingUser?.BillingProvider);
        this.externalBillingUser = billingUser;
        this.externalBillingUserFetched = true;
    }

    public isInactive(): boolean {
        return this.Model.IsInactive;
    }

    public isClerical(): boolean {
        return this.Model.Type === EmployeeType.Assistant;
    }

    public getSpecialty(): LicenceOrganisationSpecialisation {
        if (!this.cachedSpecialty || this.cachedSpecialty.Id != this.Model.LicenseOrganizationSpecializationId) // reload cachedSpecialty
            this.cachedSpecialty = cacheStore.GetSpecialtyById(this.Model.LicenseOrganizationSpecializationId);

        return this.cachedSpecialty;
    }

    public getSpecialtyString(): string {
        return LicenceOrganisationSpecialisation.getSpecialtyName(this.getSpecialty());
    }

    public shouldHaveSupervisorBasedOnTitle(): boolean {
        return Employee.shouldHaveSupervisorBasedOnTitle(this.Model);
    }

    public nameWithPrefix(): string {
        if (this.Model == null)
            return this.Name;

        let firstName = this.Model.FirstName != null ? this.Model.FirstName : "";
        let lastName = this.Model.LastName != null ? this.Model.LastName : "";

        if (this.Model.PrefixLookupId != null)
            return cacheStore.GetLocalizedValueByLookupId(this.Model.PrefixLookupId) + " " + firstName + " " + lastName;
        else
            return firstName + " " + lastName;
    }

    public getActiveSite(): SitesModel {
        return cacheStore.GetSiteById(this.Model.ActiveSiteId);
    }

    public getAllSites(): SitesModel[] {
        return this.Sites?.map(siteId => cacheStore.GetSiteById(siteId))?.filter(site => !!site) ?? [];
    }

    public hasSiteInProvince(province: CanadaState): boolean {
        return this.getAllSites().some(site => site.isSiteInProvince(province));
    }

    public isActiveSiteOntario(): boolean {
        return this.getActiveSite()?.isSiteInProvince(CanadaState.Ontario);
    }

    private cachedIsMexicoactiveSite: boolean;
    public isActiveSiteMexico(): boolean {
        if (this.cachedIsMexicoactiveSite != null)
            return this.cachedIsMexicoactiveSite;

        let activeSite = this.getActiveSite();
        return activeSite.Model.Adresses
            && activeSite.Model.Adresses.length > 0
            && activeSite.Model.Adresses[0].CountryLookupId == cacheStore.FindLookupByLookupCode("CNMEXICO").LookupID;
    }

    private cachedIsColombiaactiveSite: boolean; // !! cachedIsColombiaactiveSite stays private !!
    public isActiveSiteColombia(): boolean {
        if (this.cachedIsColombiaactiveSite != null)
            return this.cachedIsColombiaactiveSite;

        let activeSite = this.getActiveSite();
        return activeSite.Model.Adresses
            && activeSite.Model.Adresses.length > 0
            && activeSite.Model.Adresses[0].CountryLookupId == cacheStore.FindLookupByLookupCode("CNCOLOMBIA").LookupID;
    }

    public isActiveSiteBC(): boolean {
        return !!this.getActiveSite()?.isSiteInProvince(CanadaState.BritishColumbia);
    }

    public getActiveSiteCountryLookupCode(): string {
        const countryId = this.getActiveSitePrimaryAddress()?.CountryLookupId;

        return countryId
            ? cacheStore.GetLookupById(countryId).LookupCode
            : null;
    }

    public getActiveSiteStateLookupCode(): string {
        const stateId = this.getActiveSitePrimaryAddress()?.StateLookupId;

        return stateId
            ? cacheStore.GetLookupById(stateId).LookupCode
            : null;
    }

    /**
     * Get the ISO 3166-2 code for the employee's billing region (e.g. CA-QC for Quebec).
     */
    public getBillingRegionIsoCode(): string {
        // get from organization
        const organizationRuleId = this.getIssuingOrgRule()?.Id;
        const selectedOrganisationRule = cacheStore.AllLicenseOrganizationRulesById[organizationRuleId];
        if (selectedOrganisationRule)
            return `${selectedOrganisationRule.CountryCode}-${selectedOrganisationRule.StateCode}`;

        // get from active site
        const stateLookup = cacheStore.GetLookupById(this.getActiveSitePrimaryAddress()?.StateLookupId);
        return stateLookup?.LookupCode
            .replace("CanadaState_", "CA-")
            .replace("ColombiaState_", "CO-")
            .replace("MexicoState_", "MX-")
            .replace("USAState_", "US-");
    }

    public getActiveSitePrimaryAddress(): Address {
        return this.getActiveSite()?.Model?.Adresses?.firstPreferredOrDefault(p => p.IsPreferred);
    }

    private cachedIsActiveSiteQuebec: boolean; // !! cachedIsMexicoactiveSite stays private !! use the function

    public isActiveSiteQuebec(): boolean {
        if (this.cachedIsActiveSiteQuebec != null)
            return this.cachedIsActiveSiteQuebec;

        let activeSite = this.getActiveSite();
        return activeSite.Model.Adresses
            && activeSite.Model.Adresses.length > 0
            && activeSite.Model.Adresses[0].StateLookupId == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID;
    }

    public toPerson(): Person {
        return Person.FromEmployee(this.Model);
    }

    public getServices(): ServiceModel[] {
        return this.Model.ServicesOffered;
    }

    // specializations in services still use FlatLookupView instead of LicenceOrganisationSpecialisation
    public getServiceSpecializations(): FlatLookupView[] {
        if (this.serviceSpecializations != null)
            return this.serviceSpecializations;

        let serviceSpecialisations: FlatLookupView[] = [];

        let services = this.getServices();
        if (services != null) {
            for (let s of services) {
                if (s.ServiceDefinitionModel.IsAdHoc == true)
                    continue;

                // Get Distinct Specialisations
                if (!serviceSpecialisations.some(e => e.LookupID == s.ServiceDefinitionModel.Model.SpecializationLookupId))
                    serviceSpecialisations.push(cacheStore.GetLookupById(s.ServiceDefinitionModel.Model.SpecializationLookupId));
            }
        }

        return serviceSpecialisations;
    }

    public getValidationRule(phone: PhoneNumber): ValidationRule {
        let isSiteAddressValid = cacheStore.SiteCache != null && cacheStore.SiteCache[0] != null && cacheStore.SiteCache[0].Model != null && cacheStore.SiteCache[0].Model.Adresses != null && cacheStore.SiteCache[0].Model.Adresses[0] != null && cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId != null;
        let countryLookupId = isSiteAddressValid ? cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId : "";

        if (phone && phone.Number && phone.CountryLookupId != null)
            countryLookupId = phone.CountryLookupId;

        return cacheStore.validationHelper.getPhoneNumberValidationRule(countryLookupId, null);
    }

    public getValidationRuleemail(): ValidationRule {
        return cacheStore.validationHelper.getEmailValidationRule();
    }

    public setPhone(number: string): void {
        if (this.Model.Phones == null || this.Model.Phones.length == 0)
            this.Model.Phones.push(new PhoneNumber);

        this.Model.Phones[0].Number = number;
    }

    public setPhoneCountry(countryLookupId: string): void {
        if (this.Model.Phones == null || this.Model.Phones.length == 0)
            this.Model.Phones.push(new PhoneNumber());

        this.Model.Phones[0].CountryLookupId = countryLookupId;
    }

    public isPhoneValid(): boolean {
        return Employee.isPhoneValid(this.Model);
    }

    public setSendNotification(isChecked: boolean): void {
        if (this.Model.Emails != null || this.Model.Emails.length > 0)
            this.Model.IsSendNotificationByEmail = isChecked;
    }

    public onAddEmail(): void {
        let emptyEmail = Email.newEmail(ContactPointType.Work);
        emptyEmail.IsValid = this.isEmptyEmailAllowed;
        emptyEmail.IsPreferred = true;

        this.Model.Emails.push(emptyEmail);
    }

    public onAddPhone(): void {
        let emptyPhone = PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Phone);
        emptyPhone.IsValid = true;
        emptyPhone.IsPreferred = true;
        this.Model.Phones.push(emptyPhone);
    }

    public onRemoveEmail(idx: number): void {
        let emailToBeDeleted = this.Model.Emails[idx];
        this.Model.Emails.splice(idx, 1);

        for (let e of this.Model.Emails)
            this.validateEmail(e);

        if (this.Model.Emails.length == 0) {
            let emptyEmail = Email.newEmail(ContactPointType.Work);
            emptyEmail.IsValid = this.isEmptyEmailAllowed;
            this.Model.Emails.push(emptyEmail);

            this.setSendNotification(false);
        }

        if (emailToBeDeleted.IsPreferred) {
            this.Model.Emails.firstOrDefault().IsPreferred = true;
        }
    }

    public initializeEmails(): void {
        let contactPointLookups = cacheStore.GetContactPointTypeLookups();
        for (let email of this.Model.Emails || []) {
            email.renderId = sessionStore.newId().toString();
            email.AvailableContactPointTypeLookupIds = contactPointLookups;
            email.IsPreferred = false;
            this.validateEmail(email);
        }

        if (this.Model.Emails?.any())
            this.Model.Emails[0].IsPreferred = true;
    }

    public initializePhones(): void {
        for (let phone of this.Model.Phones || []) {
            phone.renderId = sessionStore.newId().toString();
            phone.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
            phone.IsPreferred = false;
            phone.IsValid = PhoneNumber.validatePhone(phone);
        }

        if (this.Model.Phones?.any())
            this.Model.Phones[0].IsPreferred = true;
    }

    public onChangeEmailType(idx: number, typeLookupId: string): void {
        let email = this.Model.Emails[idx];
        email.ContactPointTypeLookupId = typeLookupId;
        this.Model.Emails[idx] = email;
    }

    public onChangeEmailAddress(idx: number, emailAddress: string): void {
        let email = this.Model.Emails[idx];

        email.Address = emailAddress;

        this.Model.Emails[idx] = email;

        for (let e of this.Model.Emails)
            this.validateEmail(e);

        const allEmailsAreEmpty = this.Model.Emails.every(e => IsNullOrWhiteSpace(e.Address));
        if (allEmailsAreEmpty) {
            this.Model.IsSendNotificationByEmail = false;
        }
    }

    public validateEmail(email: Email): void {
        const duplicate = this.Model.Emails.filter(e => e.Address.toUpperCase() === email.Address.toUpperCase() && e.renderId !== email.renderId);
        const isEmailEmpty = !email.Address?.length;

        email.IsValid = duplicate.length == 0
        && (this.isEmptyEmailAllowed || !isEmailEmpty)
        && Email.validateEmail(email.Address);
    }

    public onChangeIsOkToContact(idx: number): void {
        let email = this.Model.Emails[idx];

        email.IsOkToContact = !email.IsOkToContact;

        this.Model.Emails[idx] = email;
    }

    public onChangeIsPreferred(idx: number): void {
        for (let e of this.Model.Emails)
            e.IsPreferred = false;

        let email = this.Model.Emails[idx];
        email.IsPreferred = !email.IsPreferred;
        this.Model.Emails[idx] = email;
    }

    public allAddressesAreValid(): boolean {
        return this.Model.Emails.every(e => e.IsValid);
    }

    public isEmailValid(): boolean {
        if (this.Model.Emails == null || this.Model.Emails.length == 0)
            return true;

        return this.allAddressesAreValid();
    }

    public getIssuingOrgRule(): LicenceOrganisationRule {
        return Employee.getIssuingOrgRule(this.Model);
    }

    public setIssuingOrgRule(licenseOrganizationRuleId: string): void {
        return Employee.setIssuingOrgRule(this.Model, licenseOrganizationRuleId);
    }

    public setpermitType(val: string): void {
        if (this.Model.Licences == null || this.Model.Licences.length == 0)
            this.Model.Licences.push(new License);

        this.Model.Licences[0].PermitTypeLookupId = val;
    }

    public setPermitNumber(val: string): void {
        if (this.Model.Licences == null || this.Model.Licences.length == 0)
            this.Model.Licences.push(new License);

        this.Model.Licences[0].LicenceNumber = val;
    }

    public getSites(): SitesModel[] {
        let siteList: SitesModel[] = [];
        let siteFinder = function(id: string): SitesModel {
            return cacheStore.SiteCache.filter(s => s.Model.Id === id)[0];
        };

        if (this.Model.AssociatedSites && this.Model.AssociatedSites.length > 0) {
            for (let x of this.Model.AssociatedSites) {
                let site = siteFinder(x);
                if (site != null)
                    siteList.push(site);
            }
        }
        return siteList;
    }

    public isLinkUrlValid(val: string): boolean {
        let urlMatchingPattern: RegExp;
        urlMatchingPattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9]\.[^\s]{2,})/;
        let isvalid = IsNullOrWhiteSpace(val);
        if (isvalid)
            return true;

        isvalid = urlMatchingPattern.test(val);
        this.linkValid = isvalid;
        return isvalid;
    }

    public getFormattedAllowedTags(allowedTags: string[]): string {
        let tag = allowedTags && allowedTags.firstOrDefault();
        return (tag || "")
            .split("*")
            .filter(p => p)
            .join(", ");
    }

    public getAggregatedTabNames(TabContext: string, lang: string): any {
        let textList = this.Model.TextList;
        if (textList?.any() !== true)
            return [];

        let filteredTextList = textList.filter(x => x.Language === lang);
        let texts = filteredTextList.filter(x => x.Context === TabContext);
        let unique_array_textList = [];
        for (let text of texts) {
            if (unique_array_textList.indexOf(text) == -1)
                unique_array_textList.push(text);
        }
        return unique_array_textList;
    }

    public static fromCompositeObject(employeeComposite: Employee): EmployeeUIModel {
        let o = new EmployeeUIModel();

        o.Model = employeeComposite;
        if (employeeComposite != null) {
            let prefixlookup = cacheStore.GetLookupById(employeeComposite.PrefixLookupId);

            o.LastName = o.Model.LastName;
            o.Id = employeeComposite.Contact_id;
            o.ContactId = employeeComposite.Contact_id;
            o.ContactFacetId = employeeComposite.ContactFacet_id;
            o.AlternativeId = employeeComposite.ContactFacet_id;
            o.Type = employeeComposite.Type;
            o.PreferedFaxAccountNumberId = employeeComposite.PreferedFaxAccountNumberId;
            o.HasPatientNoteAccess = employeeComposite.HasPatientNoteAccess;
            o.Sites = employeeComposite.AssociatedSites;
            o.DisableTimers = employeeComposite.DisableTimers;
            o.Name =
                (prefixlookup != null ? prefixlookup.GetValue() : "")
                + ` ${employeeComposite.LastName}, ${employeeComposite.FirstName}`;
            o.DrugInteractionAlertsFilterOption = employeeComposite.DrugInteractionAlertsFilterOption;
        }

        if (o.Model.Emails) {
            for (const email of o.Model.Emails) {
                const isEmailEmpty = !email.Address?.trim();
                const isEmptyEmailAllowed = !cacheStore.ClinicSettings.EnableSSOAuthentication;

                email.IsValid = (isEmptyEmailAllowed || !isEmailEmpty) && Email.validateEmail(email.Address);
            }
        }

        return o;
    }

    public static fromCompositeObjectByContactFacetId(employeeComposite: Employee): EmployeeUIModel {
        const employee = EmployeeUIModel.fromCompositeObject(employeeComposite);
        employee.Id = employeeComposite.ContactFacet_id;
        return employee;
    }

    public getFormatedGroups(): string {
        return (this.Model.Groups || [])
            .map(group => group.Name)
            .join(", ");
    }

    public getArrayAllowedTags(): Immutable.List<string> {
        let arrayAllowedTags = Immutable.List<string>();
        if (this.Model.AllowedFormTags && this.Model.AllowedFormTags.length > 0)
            arrayAllowedTags = this.Model.AllowedFormTags[0].split("*").toImmutable();
        return arrayAllowedTags;
    }

    public refreshServiceCombos(): void {
        let emp = this;
        emp.serviceComboCache = [];
        let newCurrentEditedUserOfferedServices = emp.Model.ServicesOffered != null
            ? emp.Model.ServicesOffered.map(x => x.ServiceDefinitionModel.Model.Id)
            : [];

        for (let x of newCurrentEditedUserOfferedServices) {
            let foundService: ServiceDefinitionModel = ArrayHelper.Find(
                cacheStore.ServiceDefinitionCache.toArray(),
                (serviceModel: ServiceDefinitionModel) => serviceModel.Model.Id == x
            );
            if (foundService != null) {
                let service: ServiceOrComboTableItem<IComboOrServiceItem> =
                    new ServiceOrComboTableItem<ServiceDefinitionModel>(foundService);

                emp.serviceComboCache.push(service);
            }
        }
    }

    public removeService(ServiceDefinitionId: string): void {
        let emp = this;
        emp.Model.ServicesOffered = emp.Model.ServicesOffered.filter(x => x.ServiceDefinitionModel.Model.Id != ServiceDefinitionId);
        emp.refreshServiceCombos();
    }

    public addNewServices(): void {
        let currentEditedUserOfferedServiceIds = this.serviceComboCache != null ? this.serviceComboCache.map(x => x.ServiceDefinitionId) : [];
        let newCurrentEditedUserOfferedServices = this.Model.ServicesIDsOffered != null ? this.Model.ServicesOffered.map(x => x.ServiceDefinitionModel.Model.Id) : [];
        let toAdd = newCurrentEditedUserOfferedServices.filter(x => currentEditedUserOfferedServiceIds.indexOf(x) == -1);

        for (let x of toAdd) {
            let foundService: ServiceDefinitionModel = ArrayHelper.Find(
                cacheStore.ServiceDefinitionCache.toArray(),
                (serviceModel: ServiceDefinitionModel) => serviceModel.Model.Id == x
            );

            if (foundService != null) {
                let service: ServiceOrComboTableItem<IComboOrServiceItem> =
                    new ServiceOrComboTableItem<ServiceDefinitionModel>(
                        foundService
                    );

                if (!this.serviceComboCache)
                    this.refreshServiceCombos();
                this.serviceComboCache.push(service);
            }
        }
    }

    public getLicenseDtos(usePractitionerNumberWhenRequired: boolean = true, getFirstOnly: boolean = false): LicenseDto[] {
        return Employee.getLicenseDtos(this.Model, usePractitionerNumberWhenRequired, getFirstOnly);
    }

    public getCurrentTagNames(names: Immutable.List<string>): Immutable.List<number> {
        let tagIds: Immutable.List<number> = Immutable.List([]);
        if (names != null) {
            let currentTagIds = cacheStore.radPdfTagsCache.filter(kvp => names.indexOf(kvp.Value) !== -1).map(d => d.Key);
            tagIds = Immutable.List(currentTagIds);
        }
        return tagIds;
    }

    public addNewLink(newLink: string): void {
        let usefulLinkUrl: string[] = [];

        let url = this.Model.UsefulLinks;
        if (url != null && url.length > 0) {
            let parts = url[0].split(";");
            usefulLinkUrl = parts;
            if (!usefulLinkUrl.some(x => x.toString() == newLink.trim()))
                this.Model.UsefulLinks.push(newLink.trim());
        }
        else {
            this.Model.UsefulLinks = [];
            this.Model.UsefulLinks.push(newLink.trim());
        }
    }

    public static new(): EmployeeUIModel {
        let tmpEmp = new EmployeeUIModel();

        return tmpEmp;
    }

    public static fromIntervenorInfo(licencedIntervenor: IReferralDoctorModel, titleAndSpecialty: TitleAndSpecialtyModel | null): EmployeeUIModel {
        let newUser = EmployeeUIModel.new();

        // SetSharingLabDefault
        newUser.Model.IsSharingLabs = true;

        // Gender
        if (!IsNullOrWhiteSpace(licencedIntervenor.Gender)) {
            let genderLookups = cacheStore.FindLookupsByTypeCode("Gender");
            licencedIntervenor.Gender == "M" ? newUser.Model.GenderLookupId = genderLookups.filter(x => x.LookupCode == "MALE").firstOrDefault().LookupID : newUser.Model.GenderLookupId = genderLookups.filter(x => x.LookupCode == "FEMALE").firstOrDefault().LookupID;
        }

        // Issuing organisation, licence number
        newUser.Model.Licences = [EmployeeUIModel.getLicenseFromIntervenor(licencedIntervenor)];

        // Permit Status
        if (!IsNullOrWhiteSpace(licencedIntervenor.StatusDetails)) {
            let permitStatusLookup = cacheStore.FindLookupsByTypeCode("PERMITSTATUSCODE");

            if (licencedIntervenor.StatusDetails === "Inscrit - Actif")
                newUser.Model.StatusLookupId = permitStatusLookup.filter(x => x.LookupCode === "InscritActif").firstOrDefault().LookupID;
            if (licencedIntervenor.StatusDetails === "Inscrit - Retraité")
                newUser.Model.StatusLookupId = permitStatusLookup.filter(x => x.LookupCode === "InscritRetraite").firstOrDefault().LookupID;
        }

        // Specialty
        newUser.Model.LicenseOrganizationSpecializationId = titleAndSpecialty?.licenceOrganisationSpecialisationModel?.Id;

        // Title
        newUser.setIssuingOrgRule(titleAndSpecialty?.licenceOrganisationRuleModel?.Id);

        // Prefix
        if (EmployeeTitleHelper.isDoctor(titleAndSpecialty?.licenceOrganisationRuleModel?.Title)) {
            const prefixDrLookup = cacheStore.FindLookupsByTypeCode("COMMON.INDIVIDUAL.PREFIXES").filter(x => x.LookupCode == "PREFIX.DR").firstOrDefault();
            newUser.Model.PrefixLookupId = prefixDrLookup.LookupID;
        }

        let phone = new PhoneNumber();
        phone.Number = licencedIntervenor.Phone;
        phone.CountryLookupId = cacheStore.FindLookupByLookupCode("CNCANADA").LookupID;
        newUser.Model.Phones = [phone];
        newUser.Model.FirstName = licencedIntervenor.FirstName;
        newUser.Model.LastName = licencedIntervenor.LastName;

        return newUser;
    }

    public getRamqCredentials(): { CElogin: string, AIPu: string } {
        if (!this)
            return null;

        return {
            CElogin: this.Model.CeLoginRamq,
            AIPu: this.getActiveSite().Model.AipLogin
        };
    }

    public getLicenseDto(usePractitionerNumberWhenRequired: boolean = true): LicenseDto {
        return Employee.getLicenseDto(this.Model, usePractitionerNumberWhenRequired);
    }

    public get MedicalNoteAllowlist(): IAllowlistModel {
        if (!this.Model.MedicalNoteAllowlist) {
            return {
                EmployeeId: this.Model.ContactFacet_id,
                IsActive: false,
                AllowlistType: AllowlistType.MedicalNoteAllowlist,
                MemberIds: []
            };
        }
        return this.Model.MedicalNoteAllowlist;
    }

    public set MedicalNoteAllowlist(value: IAllowlistModel) {
        this.Model.MedicalNoteAllowlist = value;
    }

    public get MedicalNoteAllowlistChanges(): IAllowlistModel {
        return this.Model.MedicalNoteAllowlistChanges;
    }

    public set MedicalNoteAllowlistChanges(value: IAllowlistModel) {
        this.Model.MedicalNoteAllowlistChanges = value;
    }

    private static getLicenseFromIntervenor(licencedIntervenor: IReferralDoctorModel): License {
        return new License({
            LicenceNumber: licencedIntervenor.LicenceNumber,
            IssuingLicenseOrganizationRuleId: licencedIntervenor.LicenceOrganisationRuleId
        });
    }

    public isQuebecObstetricalFileEnabled(): boolean {
        return this.isActiveSiteQuebec() && cacheStore.ClinicSettings.EnableQuebecObstetricalFile;
    }

    public isOntarioObstetricalFileEnabled(): boolean {
        return this.isActiveSiteOntario() && cacheStore.ClinicSettings.EnableOntarioObstetricalFile;
    }

    public isBritishColumbiaObstetricalFileEnabled(): boolean {
        return this.isActiveSiteBC() && cacheStore.ClinicSettings.EnableBritishColumbiaObstetricalFile;
    }

    public hasAgreedToTermsAndConditions(serviceName: TermsAndConditionsServiceNames): boolean {
        return cacheStore.getEmployeeTermsAndConditionsAgreement()
            .some(termsAndConditions =>
                termsAndConditions.ServiceName === serviceName
            );
    }
}

export { EmployeeUIModel };
